import { useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Check, Clock, Sun, Coffee, Moon, Bed, Syringe, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { getSettings, getSupplementsByDate, saveSupplementLog, getAllSupplementLogs } from '../lib/db';
import { SUPPLEMENTS, getDateString, formatDate } from '../lib/constants';

export const Supplements = () => {
  const [settings, setSettings] = useState(null);
  const [takenSupps, setTakenSupps] = useState({});
  const [weeklyCompliance, setWeeklyCompliance] = useState(0);
  const [loading, setLoading] = useState(true);

  const today = getDateString();

  const loadData = useCallback(async () => {
    try {
      const settingsData = await getSettings();
      setSettings(settingsData);
      
      const todaySupps = await getSupplementsByDate(today);
      const takenMap = {};
      todaySupps.forEach(s => {
        takenMap[s.suppId] = s.timestamp;
      });
      setTakenSupps(takenMap);

      // Calculate weekly compliance
      const allSupps = await getAllSupplementLogs();
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      
      const weekSupps = allSupps.filter(s => new Date(s.date) >= weekAgo);
      const totalPossible = 7 * getTotalSupplementCount();
      const compliance = totalPossible > 0 
        ? Math.round((weekSupps.length / totalPossible) * 100) 
        : 0;
      setWeeklyCompliance(compliance);
    } catch (error) {
      console.error('Error loading supplements:', error);
    } finally {
      setLoading(false);
    }
  }, [today]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getTotalSupplementCount = () => {
    return (
      SUPPLEMENTS.morning.length +
      SUPPLEMENTS.lunch.length +
      SUPPLEMENTS.dinner.length +
      SUPPLEMENTS.bedtime.length +
      SUPPLEMENTS.peptides.length
    );
  };

  const toggleSupplement = async (suppId, timeSlot) => {
    if (takenSupps[suppId]) {
      // Already taken - do nothing (can't untake)
      return;
    }

    const log = {
      id: uuidv4(),
      date: today,
      suppId,
      timeSlot,
      timestamp: new Date().toISOString(),
    };

    await saveSupplementLog(log);
    setTakenSupps(prev => ({ ...prev, [suppId]: log.timestamp }));
    toast.success('Supplement logged!');
  };

  const getExpectedTime = (timeSlot) => {
    const times = {
      morning: 8,
      lunch: 13,
      dinner: 19,
      bedtime: 22,
    };
    return times[timeSlot] || 12;
  };

  const isLate = (timeSlot) => {
    const now = new Date();
    const expectedHour = getExpectedTime(timeSlot);
    return now.getHours() > expectedHour + 2;
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const todayTaken = Object.keys(takenSupps).length;
  const todayTotal = getTotalSupplementCount();
  const todayPercent = Math.round((todayTaken / todayTotal) * 100);

  return (
    <div className="space-y-6" data-testid="supplements-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-gray-500">
            {formatDate(today)}
          </p>
          <h1 className="text-2xl font-black tracking-tight">Supplements</h1>
        </div>
        <div className="text-right">
          <p className="text-2xl font-black font-mono text-[#00E676]">{todayPercent}%</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Today</p>
        </div>
      </div>

      {/* Weekly Compliance */}
      <div className="bg-[#24243e] border border-white/5 rounded-2xl p-4" data-testid="weekly-compliance">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-bold">Weekly Compliance</span>
          <span className="font-mono font-bold text-[#00E676]">{weeklyCompliance}%</span>
        </div>
        <div className="h-2 bg-[#161625] rounded-full overflow-hidden">
          <div 
            className="h-full bg-[#00E676] transition-all"
            style={{ width: `${weeklyCompliance}%` }}
          />
        </div>
      </div>

      {/* Time-based Columns */}
      <div className="grid grid-cols-2 gap-3">
        {/* Morning */}
        <SupplementColumn
          title="Morning"
          icon={<Coffee className="text-yellow-400" size={16} />}
          supplements={SUPPLEMENTS.morning}
          takenSupps={takenSupps}
          onToggle={(id) => toggleSupplement(id, 'morning')}
          isLate={isLate('morning')}
          timeSlot="morning"
        />

        {/* Lunch */}
        <SupplementColumn
          title="Lunch"
          icon={<Sun className="text-orange-400" size={16} />}
          supplements={SUPPLEMENTS.lunch}
          takenSupps={takenSupps}
          onToggle={(id) => toggleSupplement(id, 'lunch')}
          isLate={isLate('lunch')}
          timeSlot="lunch"
        />

        {/* Dinner */}
        <SupplementColumn
          title="Dinner"
          icon={<Moon className="text-blue-400" size={16} />}
          supplements={SUPPLEMENTS.dinner}
          takenSupps={takenSupps}
          onToggle={(id) => toggleSupplement(id, 'dinner')}
          isLate={isLate('dinner')}
          timeSlot="dinner"
        />

        {/* Bedtime */}
        <SupplementColumn
          title="Bedtime"
          icon={<Bed className="text-purple-400" size={16} />}
          supplements={SUPPLEMENTS.bedtime}
          takenSupps={takenSupps}
          onToggle={(id) => toggleSupplement(id, 'bedtime')}
          isLate={isLate('bedtime')}
          timeSlot="bedtime"
        />
      </div>

      {/* Peptides Section */}
      <div className="bg-[#24243e] border border-white/5 rounded-2xl p-5" data-testid="peptides-section">
        <div className="flex items-center gap-2 mb-4">
          <Syringe className="text-cyan-400" size={18} />
          <h2 className="text-sm font-black uppercase tracking-wider">Peptides</h2>
        </div>
        
        <div className="space-y-2">
          {SUPPLEMENTS.peptides.map(peptide => {
            const isTaken = !!takenSupps[peptide.id];
            
            return (
              <button
                key={peptide.id}
                onClick={() => toggleSupplement(peptide.id, 'peptides')}
                disabled={isTaken}
                className={`w-full flex items-center justify-between p-3 rounded-xl transition-all active:scale-[0.98] ${
                  isTaken 
                    ? 'bg-[#00E676]/10 border border-[#00E676]/30' 
                    : 'bg-[#161625] border border-white/5 hover:border-white/10'
                }`}
                data-testid={`peptide-${peptide.id}`}
              >
                <div className="text-left">
                  <p className="font-medium text-sm">{peptide.name}</p>
                  <p className="text-xs text-gray-500">{peptide.dosage} • {peptide.frequency}</p>
                </div>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  isTaken ? 'bg-[#00E676] text-black' : 'border-2 border-gray-600'
                }`}>
                  {isTaken && <Check size={16} strokeWidth={3} />}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const SupplementColumn = ({ title, icon, supplements, takenSupps, onToggle, isLate, timeSlot }) => {
  const allTaken = supplements.every(s => takenSupps[s.id]);
  const anyMissed = isLate && !allTaken;

  return (
    <div 
      className={`bg-[#24243e] border rounded-2xl p-4 ${
        allTaken 
          ? 'border-[#00E676]/30' 
          : anyMissed 
            ? 'border-yellow-500/30' 
            : 'border-white/5'
      }`}
      data-testid={`supp-column-${timeSlot}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-xs font-black uppercase tracking-wider">{title}</span>
        </div>
        {anyMissed && (
          <AlertCircle size={14} className="text-yellow-500" />
        )}
      </div>
      
      <div className="space-y-2">
        {supplements.map(supp => {
          const isTaken = !!takenSupps[supp.id];
          const timestamp = takenSupps[supp.id];
          
          return (
            <button
              key={supp.id}
              onClick={() => onToggle(supp.id)}
              disabled={isTaken}
              className={`w-full flex items-center gap-2 p-2 rounded-lg transition-all active:scale-[0.98] ${
                isTaken 
                  ? 'bg-[#00E676]/10' 
                  : anyMissed && !isTaken 
                    ? 'bg-yellow-500/10' 
                    : 'bg-[#161625] hover:bg-[#1a1a2e]'
              }`}
              data-testid={`supp-${supp.id}`}
            >
              <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                isTaken 
                  ? 'bg-[#00E676] text-black' 
                  : 'border border-gray-600'
              }`}>
                {isTaken && <Check size={12} strokeWidth={3} />}
              </div>
              <div className="text-left flex-1 min-w-0">
                <p className="text-xs font-medium truncate">{supp.name}</p>
                <p className="text-[10px] text-gray-500">{supp.dosage}</p>
              </div>
              {timestamp && (
                <Clock size={10} className="text-gray-500 flex-shrink-0" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default Supplements;
